import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";

import { usePutChangeConsumerPasswordMutation } from "apis/User";

const ChangePasswordModal = ({ handleClose, t }) => {
  const [
    {
      isCurrentPasswordVisible,
      isNewPasswordVisible,
      isConfirmPasswordVisible,
    },
    setPasswordVisible,
  ] = useState({
    isCurrentPasswordVisible: false,
    isNewPasswordVisible: false,
    isConfirmPasswordVisible: false,
  });

  const [changeUserPassword, { isLoading }] =
    usePutChangeConsumerPasswordMutation();

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Current password is required"),
      newPassword: Yup.string().required("New password is required"),
      confirmPassword: Yup.string()
        .required("Confirm password is required")
        .oneOf(
          [Yup.ref("newPassword")],
          "New password and confirm password must be same"
        ),
    }),
    onSubmit: async ({ newPassword, currentPassword }) => {
      try {
        await changeUserPassword(
          {
            newPassword,
            currentPassword,
          },
          false,
          true
        );
        handleClose();
      } catch (error) {}
    },
  });

  const { handleSubmit } = formik;

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      maxWidth="md"
      title="Change Password"
    >
      <div className="mt-3">
        <Grid container columnSpacing={2}>
          <Grid item xs={12}>
            <InputFieldText
              label={t("Current Password") + "*"}
              placeholder="Enter Current Password"
              name="currentPassword"
              type={isCurrentPasswordVisible ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setPasswordVisible((prev) => ({
                          ...prev,
                          isCurrentPasswordVisible: !isCurrentPasswordVisible,
                        }))
                      }
                    >
                      {isCurrentPasswordVisible ? (
                        <VisibilityOffIcon fontSize="small" color="primary" />
                      ) : (
                        <RemoveRedEyeIcon fontSize="small" color="primary" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              formik={formik}
            />
          </Grid>
          <Grid item xs={12}>
            <InputFieldText
              label={t("New Password") + "*"}
              placeholder="Enter New Password"
              name="newPassword"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setPasswordVisible((prev) => ({
                          ...prev,
                          isNewPasswordVisible: !isNewPasswordVisible,
                        }))
                      }
                    >
                      {isNewPasswordVisible ? (
                        <VisibilityOffIcon fontSize="small" color="primary" />
                      ) : (
                        <RemoveRedEyeIcon fontSize="small" color="primary" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              type={isNewPasswordVisible ? "text" : "password"}
              formik={formik}
            />
          </Grid>

          <Grid item xs={12}>
            <InputFieldText
              label={t("Confirm Password") + "*"}
              placeholder="Enter Confirm Password"
              name="confirmPassword"
              type={isConfirmPasswordVisible ? "text" : "password"}
              formik={formik}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setPasswordVisible((prev) => ({
                          ...prev,
                          isConfirmPasswordVisible: !isConfirmPasswordVisible,
                        }))
                      }
                    >
                      {isConfirmPasswordVisible ? (
                        <VisibilityOffIcon fontSize="small" color="primary" />
                      ) : (
                        <RemoveRedEyeIcon fontSize="small" color="primary" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} className="text-end mt-3">
            <Button
              onClick={handleSubmit}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Submit")}
              {isLoading && <CircularProgress size={20} />}
            </Button>
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};

ChangePasswordModal.propTypes = {
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(ChangePasswordModal);
